import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext'; // 导入 ThemeProvider
import { Toaster } from 'react-hot-toast';
import Layout from './components/Layout'; // 导入独立的 Layout 组件
import LoginForm from './components/LoginForm';
import LicenseGenerator from './components/LicenseGenerator';
import LicenseHistory from './components/LicenseHistory';
import ChangePassword from './components/ChangePassword';
import UserProfile from './pages/UserProfile';
import UserSettings from './pages/UserSettings';
import SystemConfiguration from './pages/SystemConfiguration';
import Home from './pages/Home';
import ErrorBoundary from './components/ErrorBoundary';
import NotFound from './components/NotFound';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import DefaultPasswordWarning from './components/DefaultPasswordWarning';
import UpdateService from './services/updateService';
import './components/UpdateNotification'; // 确保组件被注册

// 开发环境下导入调试工具
if (import.meta.env.DEV) {
  import('./utils/sessionDebug.js');
}

// 保护路由组件
const ProtectedRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="system-spinner" aria-label="正在加载"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} />;
  }

  return <Outlet />;
};

// 管理员路由组件
const AdminRoute = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === 'admin' || user?.isAdmin;
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="system-spinner" aria-label="正在加载"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} />;
  }

  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  return <Outlet />;
};

// 添加更新检查组件
const UpdateChecker = () => {
  const { isAuthenticated } = useAuth();
  
  useEffect(() => {
    let updateInterval = null;
    
    // 只有登录后才开始检查更新
    if (isAuthenticated) {
      // 开始更新检查，每60分钟检查一次
      updateInterval = UpdateService.startUpdatePolling(60);
    }
    
    return () => {
      if (updateInterval) {
        UpdateService.stopUpdatePolling(updateInterval);
      }
    };
  }, [isAuthenticated]);
  
  return null; // 这是一个功能性组件，不渲染任何UI
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider> {/* 添加 ThemeProvider */}
          {/* 添加更新检查器 */}
          <UpdateChecker />
          
          {/* 会话超时警告 */}
          <SessionTimeoutWarning />

          {/* 默认密码安全提醒 */}
          <DefaultPasswordWarning />
          
          <Toaster 
            position="top-center" 
            toastOptions={{
              duration: 4000,
              style: {
                background: 'rgba(255, 255, 255, 0.92)',
                color: '#1d1d1f',
                padding: '14px 16px',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '14px',
                fontSize: '14px',
                fontWeight: '500',
                boxShadow: '0 18px 50px rgba(0, 0, 0, 0.16)',
                backdropFilter: 'blur(24px)',
                maxWidth: '500px',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#0071e3',
                  secondary: '#ffffff',
                }
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#ff3b30',
                  secondary: '#ffffff',
                }
              },
              loading: {
                iconTheme: {
                  primary: '#0071e3',
                  secondary: 'rgba(0, 113, 227, 0.16)',
                }
              }
            }} 
          />
          
          <Routes>
            {/* 登录页 - 无需布局 */}
            <Route path="/login" element={<LoginForm />} />
            
            {/* 受保护的路由 */}
            <Route element={<ProtectedRoute />}>
              {/* 使用新的Layout组件 */}
              <Route element={<Layout />}>
                {/* 首页 */}
                <Route index element={<Home />} />
                {/* 许可证生成器页面 */}
                <Route path="generate" element={<LicenseGenerator />} />
                
                {/* 许可证历史记录 */}
                <Route path="licenses/:service" element={<LicenseHistory />} />
                
                {/* 用户中心路由 */}
                <Route path="user/profile" element={<UserProfile />} />
                <Route path="user/settings" element={<UserSettings />} />
                <Route path="change-password" element={<ChangePassword />} />
                
                {/* 管理员路由 */}
                <Route path="admin">
                  <Route element={<AdminRoute />}>
                    <Route index element={<Navigate to="/admin/configuration" replace />} />
                    <Route path="configuration" element={<SystemConfiguration />} />
                    <Route path="*" element={<Navigate to="/admin/configuration" replace />} />
                  </Route>
                </Route>
              </Route>
            </Route>
            
            {/* 未找到页面 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
